import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map } from 'rxjs';
import { MaterialModule } from '../../material.module';
import { ProjectDetailsService, TestAttachmentDto } from '../../services/project-details.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';

export interface TestAttachmentsDialogData {
  testInputMasterId: number;
  testInputMasterIds?: number[];
  slurryId?: number;
  title: string;
}

@Component({
  selector: 'app-test-attachments-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent],
  templateUrl: './test-attachments-dialog.component.html',
  styleUrls: ['./test-attachments-dialog.component.css']
})
export class TestAttachmentsDialogComponent implements OnInit {
  attachments: TestAttachmentDto[] = [];
  isLoading = false;
  isUploading = false;
  isDragOver = false;
  loadErrorMessage = '';
  actionErrorMessage = '';
  selectedFile: File | null = null;
  uploadDescription = '';
  readonly deletingAttachmentIds = new Set<number>();
  readonly attachmentTargets: Array<{ id: number; label: string }>;
  selectedAttachmentMasterId: number;

  constructor(
    private readonly projectDetailsService: ProjectDetailsService,
    private readonly dialogRef: MatDialogRef<TestAttachmentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: TestAttachmentsDialogData
  ) {
    const testInputMasterIds = Array.from(new Set([
      data.testInputMasterId,
      ...(data.testInputMasterIds ?? [])
    ]));
    this.attachmentTargets = testInputMasterIds.map((id, index) => ({ id, label: `Reading ${index + 1}` }));
    this.selectedAttachmentMasterId = data.testInputMasterId;
  }

  ngOnInit(): void {
    this.loadAttachments();
  }

  close(): void {
    if (this.isUploading) {
      return;
    }

    this.dialogRef.close();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setSelectedFile(input.files?.[0] ?? null);
    this.actionErrorMessage = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.setSelectedFile(event.dataTransfer?.files?.[0] ?? null);
    this.actionErrorMessage = '';
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.isDragOver = false;
  }

  uploadSelectedFile(): void {
    if (!this.selectedFile || this.isUploading) {
      return;
    }

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('description', this.uploadDescription.trim());

    this.actionErrorMessage = '';
    this.isUploading = true;
    this.dialogRef.disableClose = true;

    this.projectDetailsService.uploadTestAttachment(this.selectedAttachmentMasterId, formData).subscribe({
      next: () => {
        this.selectedFile = null;
        this.uploadDescription = '';
        this.isUploading = false;
        this.dialogRef.disableClose = false;
        this.loadAttachments();
      },
      error: (err) => {
        console.error('Failed to upload test attachment:', err);
        this.actionErrorMessage = 'Failed to upload attachment.';
        this.isUploading = false;
        this.dialogRef.disableClose = false;
      }
    });
  }

  deleteAttachment(attachment: TestAttachmentDto): void {
    if (this.deletingAttachmentIds.has(attachment.id)
      || !window.confirm(`Are you sure you want to delete "${attachment.fileName}"?`)) {
      return;
    }

    this.actionErrorMessage = '';
    this.deletingAttachmentIds.add(attachment.id);
    this.projectDetailsService.deleteTestAttachment(attachment.ownerId, attachment.id).subscribe({
      next: () => {
        this.deletingAttachmentIds.delete(attachment.id);
        this.loadAttachments();
      },
      error: (err) => {
        console.error('Failed to delete test attachment:', err);
        this.actionErrorMessage = 'Failed to delete attachment.';
        this.deletingAttachmentIds.delete(attachment.id);
      }
    });
  }

  downloadAttachment(attachment: TestAttachmentDto): void {
    this.actionErrorMessage = '';
    this.projectDetailsService.downloadTestAttachment(attachment.ownerId, attachment.id).subscribe({
      next: (blob) => {
        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = attachment.fileName;
        link.click();
        URL.revokeObjectURL(fileUrl);
      },
      error: (err) => {
        console.error('Failed to download test attachment:', err);
        this.actionErrorMessage = 'Failed to download attachment.';
      }
    });
  }

  openAttachment(attachment: TestAttachmentDto): void {
    this.actionErrorMessage = '';

    // Open the tab during the click event so browsers do not treat it as an unsolicited popup.
    const attachmentTab = window.open('', '_blank');
    if (!attachmentTab) {
      this.actionErrorMessage = 'Unable to open attachment. Please allow pop-ups for this site.';
      return;
    }

    attachmentTab.opener = null;
    this.projectDetailsService.downloadTestAttachment(attachment.ownerId, attachment.id).subscribe({
      next: (blob) => {
        const fileUrl = URL.createObjectURL(blob);
        if (blob.type === 'application/octet-stream') {
          attachmentTab.close();
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = attachment.fileName;
          link.click();
          URL.revokeObjectURL(fileUrl);
          return;
        }

        attachmentTab.addEventListener('load', () => URL.revokeObjectURL(fileUrl), { once: true });
        attachmentTab.location.href = fileUrl;
      },
      error: (err) => {
        attachmentTab.close();
        console.error('Failed to open test attachment:', err);
        this.actionErrorMessage = 'Failed to open attachment.';
      }
    });
  }

  private loadAttachments(): void {
    this.isLoading = true;
    this.loadErrorMessage = '';
    const ownerIds = this.attachmentTargets.map(target => target.id);
    const request$ = ownerIds.length > 1 && this.data.slurryId != null
      ? this.projectDetailsService.getTestAttachmentsBySlurryId(this.data.slurryId).pipe(
        map(attachments => attachments.filter(attachment => ownerIds.includes(attachment.ownerId)))
      )
      : this.projectDetailsService.getTestAttachmentsByTestInputMasterId(this.data.testInputMasterId);
    request$.subscribe({
      next: (attachments) => {
        this.attachments = attachments.sort((left, right) => left.fileName.localeCompare(right.fileName));
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load test attachments:', err);
        this.loadErrorMessage = 'Failed to load attachments.';
        this.isLoading = false;
      }
    });
  }

  private setSelectedFile(file: File | null): void {
    this.selectedFile = file;
    this.isDragOver = false;
  }
}
